import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Icon from "../../icons";
import { hideDialogueBox } from "../DialogueBox/actions";
import { showToast } from "../../containers/SpockViewPage/actions/action";
import CKTextEditor from "../CKTextEditor";
import LoaderComponent from "../Loader";
import { fetchShareProposalDetails, shareProposal } from "../../containers/WorkkardDetail/api";
import PublicApiExzeoUpload from "../PublicApiExzeoUpload";
import { MAX_UPLOAD_FILE_SIZE, INVALID_FILES_TO_UPLOAD, FILE_UPLOAD_URL, StringConstants } from "../../containers/WorkkardDetail/constants";
import "./styles.scss";

const PROPOSAL_NOT_SENT_MESSAGE = "Could not send proposal. Please try again.";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cleanEmails = (emails) => Array.from(new Set((emails || []).map((email) => String(email).trim()).filter(Boolean)));

const EmailChipInput = ({ label, emails, onChange }) => {
  const [text, setText] = useState("");

  // every typed address becomes a chip; the ones that are not valid emails are shown in red
  const commit = () => {
    const email = text.trim().replace(/[,;]$/, "").trim();
    if (!email) return;
    if (!emails.includes(email)) onChange([...emails, email]);
    setText("");
  };

  const onKeyDown = (e) => {
    if (["Enter", ",", ";", " "].includes(e.key)) {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && !text && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  return (
    <div className="proposalRow">
      <label>{label}</label>
      <div className="proposalField chipField">
        {emails.map((email) => (
          <span className={`emailChip${EMAIL_REGEX.test(email) ? "" : " invalid"}`} key={email}>
            {email}
            <a onClick={() => onChange(emails.filter((e) => e !== email))}>×</a>
          </span>
        ))}
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
        />
      </div>
    </div>
  );
};


// The body is the full quinjet email template. The email layout is built from nested single-cell
// tables (page card, borders, padding) which look like boxes inside the editor, so they are
// replaced by plain paragraphs. Real data tables (several cells per row) are kept as they are.
const isLayoutTable = (table) => {
  const rows = Array.from(table.rows);
  return rows.length > 0 && rows.every((row) => row.cells.length === 1);
};

const extractBodyContent = (html = "") => {
  if (!/<(html|body)[\s>]/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const root = doc.body;
  root.querySelectorAll("style, script, title, meta").forEach((node) => node.remove());
  root.querySelectorAll("img").forEach((img) => {
    if (!/^(https?:|data:|\/)/i.test(img.getAttribute("src") || "")) img.remove();
  });
  root.querySelectorAll("a").forEach((a) => {
    if (!a.textContent.trim()) a.remove();
  });

  let table;
  while ((table = Array.from(root.querySelectorAll("table")).find(isLayoutTable))) {
    const blocks = Array.from(table.rows).map((row) => {
      const cell = row.cells[0];
      if (cell.querySelector("table")) return Array.from(cell.childNodes);
      if (!cell.textContent.replace(/\u00a0/g, "").trim()) return null;
      const p = doc.createElement("p");
      p.append(...Array.from(cell.childNodes));
      return [p];
    });
    table.replaceWith(...blocks.filter(Boolean).flat());
  }

  root.querySelectorAll("[style], [class], [align], [valign]").forEach((el) => {
    if (el.closest("table")) return;
    ["style", "class", "align", "valign"].forEach((attr) => el.removeAttribute(attr));
  });
  root.querySelectorAll("p").forEach((p) => {
    while (p.firstChild && p.firstChild.nodeName === "BR") p.firstChild.remove();
    while (p.lastChild && p.lastChild.nodeName === "BR") p.lastChild.remove();
  });
  return root.innerHTML.trim();
};

// attachments the backend returns for the carriers' notes -> shape the upload list works with
const toFileRow = (file) => ({
  guid: file.fileGuid,
  name: file.fileName,
  fileName: file.fileName,
});

const ShareProposal = ({ workkardId, workkardNumber = "" }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [address, setAddress] = useState("");
  const [from, setFrom] = useState("");
  const [proposalWorkkardNumber, setProposalWorkkardNumber] = useState(workkardNumber);
  const [toList, setToList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [subjectText, setSubjectText] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadedData, setUploadedData] = useState([]);
  const [fileRemoving, setFileRemoving] = useState(false);

  useEffect(() => {
    let active = true;
    fetchShareProposalDetails({ cId: workkardId })
      .then((res) => {
        if (!active) return;
        const d = res?.data || {};
        setCustomerName(d.customerName || "");
        setAddress(d.address || "");
        setFrom(d.from || "");
        setProposalWorkkardNumber(d.workkardNumber || workkardNumber);
        setToList(cleanEmails(d.to));
        setCcList(cleanEmails(d.cc));
        setSubjectText(d.subject || "");
        setBodyHtml(extractBodyContent(d.body || ""));
        const files = (d.attachments || []).map(toFileRow);
        setAttachments(files);
        setUploadedData(files);
      })
      .catch(() => active && setErrorMsg("Unable to load proposal details."))
      .finally(() => active && setIsLoading(false));
    return () => { active = false; };
  }, [workkardId]);

  // same wiring the note panel uses for PublicApiExzeoUpload
  const handleUploadFiles = (data) => {
    setUploadedData(data);
    setFileRemoving(true);
  };

  const close = () => dispatch(hideDialogueBox());
  const isBodyEmpty = !bodyHtml.replace(/<(?!img\b)[^>]*>|&nbsp;|\s/gi, "");
  const canSend =
    !isLoading &&
    !isSending &&
    toList.length > 0 &&
    toList.every((email) => EMAIL_REGEX.test(email)) &&
    ccList.every((email) => EMAIL_REGEX.test(email)) &&
    subjectText.trim() !== "" &&
    !isBodyEmpty;

  console.log("[ShareProposal] DEBUG " + JSON.stringify({
    canSend,
    isLoading,
    isSending,
    toList,
    ccList,
    invalidTo: toList.filter((email) => !EMAIL_REGEX.test(email)),
    invalidCc: ccList.filter((email) => !EMAIL_REGEX.test(email)),
    subject: subjectText,
    isBodyEmpty,
    bodyLength: bodyHtml.length,
    bodyStart: bodyHtml.slice(0, 150),
  }));

  const send = () => {
    if (!canSend) return;
    setIsSending(true);
    setErrorMsg(null);
    shareProposal({
      cId: workkardId,
      workkardNumber: proposalWorkkardNumber,
      from,
      to: toList,
      cc: ccList,
      subject: subjectText,
      body: bodyHtml,
      attachments: attachments.map(({ guid, name }) => ({ fileGuid: guid, fileName: name })),
    })
      .then((res) => {
        // the gateway returns the activity that got the proposal note; without one there is nothing to view
        const noteActivityId = res?.activityId ?? res?.data?.activityId;
        close();
        dispatch(showToast({
          message: "Proposal shared successfully.",
          type: "success",
          autoClose: true,
          delayTime: 5000,
          ...(noteActivityId ? {
            showActions: true,
            entityType: StringConstants.activity,
            cId: workkardId,
            actId: noteActivityId,
          } : {}),
        }));
      })
      .catch(() => {
        setErrorMsg(PROPOSAL_NOT_SENT_MESSAGE);
        dispatch(showToast({
          message: PROPOSAL_NOT_SENT_MESSAGE,
          type: "error",
          autoClose: true,
          delayTime: 5000,
        }));
        setIsSending(false);
      });
  };

  return (
    <>
      <div className="modalHeader alignMiddle headSticky">
        <h1>Share Proposal</h1>
        <a className="close">
          <Icon name="close" onClick={close} className="icon" />
        </a>
      </div>

      <div className="modalBody">
        <div className="shareProposal">
        {isLoading ? <LoaderComponent /> : <>
        {errorMsg && <p className="error">{errorMsg}</p>}
        <ul className="proposalInfo">
          <li><label className="status wkNumber">{workkardNumber}</label></li>
          {customerName && <li>{customerName}</li>}
          {address && <li>{address}</li>}
        </ul>

        <div className="proposalRow">
          <label>From</label>
          <div className="proposalField readOnly">{from}</div>
        </div>
        <EmailChipInput label="To" emails={toList} onChange={setToList} />
        <EmailChipInput label="Cc" emails={ccList} onChange={setCcList} />
        <div className="proposalRow">
          <label>Subject</label>
          <div className="proposalField">
            <input type="text" value={subjectText} onChange={(e) => setSubjectText(e.target.value)} />
          </div>
        </div>

        <div className="proposalEditor">
          <CKTextEditor editorData={bodyHtml} onChange={setBodyHtml} hideBorders richToolbar height={420} />
        </div>

        <div className="proposalAttachments">
          <PublicApiExzeoUpload
            url={FILE_UPLOAD_URL}
            saveAndUpdateFileCaption={setAttachments}
            maxSize={MAX_UPLOAD_FILE_SIZE}
            inValidFiles={INVALID_FILES_TO_UPLOAD}
            multiple={true}
            hideCaption
            uploadedData={uploadedData}
            onRemoveFile={handleUploadFiles}
            removing={fileRemoving}
            setFalseRemovingState={() => setFileRemoving(false)}
          />
        </div>
        </>}
        </div>
      </div>

      <div className="modalFooter">
        <button type="button" className={`btn buttonGradient ${canSend ? "" : "disabled"}`} disabled={!canSend} onClick={send}>Send</button>
        <button type="button" className="btn link" onClick={close}>Cancel</button>
      </div>
    </>
  );
};

export default ShareProposal;
